import { useState } from 'react';
import { Link } from 'react-router-dom';
import { latestArticles } from '@/mocks/home';

export default function NewsFlash() {
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [newsletterError, setNewsletterError] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const phoneAlt = formData.get('phone_alt') as string;
    if (phoneAlt && phoneAlt.trim() !== '') {
      setNewsletterStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u01253pcjqs2fcsjo0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setNewsletterStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && serverMsg.toLowerCase().includes('spam')) {
          setNewsletterStatus('success');
          form.reset();
        } else {
          setNewsletterStatus('error');
          setNewsletterError(serverMsg || '订阅失败，请稍后重试');
        }
      }
    } catch {
      setNewsletterStatus('error');
      setNewsletterError('网络错误，请检查连接后重试');
    }
  };

  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-8">
              <div>
                <span className="inline-block px-4 py-1.5 rounded-full bg-accent-50 text-accent-700 text-xs font-medium mb-3">
                  资讯快报
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  最新动态
                </h2>
              </div>
              <Link
                to="/category/news"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                更多资讯 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="space-y-4">
              {latestArticles.map((article, idx) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      {idx === 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">最新</span>
                      )}
                      <span className="text-xs text-foreground-500">{article.category}</span>
                      <span className="text-xs text-foreground-400">{article.date}</span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h4 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="p-6 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-background-50">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-50/20 mb-4">
                  <i className="ri-notification-3-line text-xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2">订阅资讯快报</h3>
                <p className="text-sm text-background-50/80 mb-4 leading-relaxed">
                  订阅亚洲在线的每日资讯快报，第一时间获取亚洲数字生活最新动态和独家优惠信息。
                </p>
                <form
                  className="flex gap-2"
                  data-readdy-form="newsletter"
                  onSubmit={handleNewsletterSubmit}
                >
                  <input
                    type="email"
                    name="email"
                    placeholder="输入邮箱地址"
                    className="flex-1 px-4 py-2.5 rounded-lg text-sm text-foreground-900 bg-background-50 border-0 outline-none"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2.5 bg-foreground-950 hover:bg-foreground-900 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    订阅
                  </button>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                </form>
                {newsletterStatus === 'success' && (
                  <p className="mt-3 text-xs text-accent-300">
                    <i className="ri-checkbox-circle-line mr-1"></i>订阅成功！感谢您的关注。
                  </p>
                )}
                {newsletterStatus === 'error' && (
                  <p className="mt-3 text-xs text-red-300">
                    <i className="ri-error-warning-line mr-1"></i>{newsletterError || '订阅失败，请稍后重试'}
                  </p>
                )}
              </div>

              <div className="mt-5 p-5 rounded-xl bg-background-50 border border-background-200/70">
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-3">热门标签</h4>
                <div className="flex flex-wrap gap-2">
                  {['AI人工智能', '跨境电商', '流媒体', 'Web3', '健康科技', '元宇宙', '5G网络', '数字支付'].map((tag) => (
                    <Link
                      key={tag}
                      to="/category/tech"
                      className="px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-600 hover:bg-accent-50 hover:text-accent-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}